import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError, isImpersonating, clearImpToken, isSandbox, setImpToken } from "@/lib/api";

const AuthContext = createContext(null);

// Per-device memory of the last context (role) a multi-role user chose.
const lastRoleKey = (uid) => `sap_active_role_${uid}`;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauth, object = auth
  const [error, setError] = useState("");

  // If this device remembers a preferred context and the user still holds it,
  // silently restore it (even if their server-side active_role was changed elsewhere).
  const applyPreferredRole = useCallback(async (u) => {
    try {
      if (!u || u.kind !== "user") return u;
      const roles = u.roles || [u.role];
      if (roles.length < 2) return u;
      const pref = localStorage.getItem(lastRoleKey(u.id));
      if (pref && roles.includes(pref) && pref !== u.role) {
        const { data } = await api.post("/auth/switch-role", { role: pref });
        return data;
      }
    } catch (_) { /* fall through to server value */ }
    return u;
  }, []);

  const fetchMe = useCallback(async () => {
    // Sandbox tabs ignore the shared cookie session entirely: no token here = signed out.
    if (isSandbox() && !isImpersonating()) { setUser(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(await applyPreferredRole(data));
    } catch (e) {
      setUser(false);
    }
  }, [applyPreferredRole]);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.mfa_required) return data; // caller shows the MFA challenge
      const u = await applyPreferredRole(data.user);
      setUser(u);
      return u;
    } catch (e) {
      const m = formatApiError(e); setError(m); throw new Error(m);
    }
  };

  const passkeyLogin = async () => {
    setError("");
    try {
      const { startAuthentication } = await import("@simplewebauthn/browser");
      const { data: opts } = await api.post("/auth/passkey/login/options", { origin: window.location.origin });
      const { challenge_token } = opts;
      const cred = await startAuthentication({ optionsJSON: opts });
      const { data } = await api.post("/auth/passkey/login/verify", { ...cred, challenge_token, origin: window.location.origin });
      const u = await applyPreferredRole(data.user);
      setUser(u);
      return u;
    } catch (e) {
      const m = e?.name === "NotAllowedError" ? "Fingerprint sign-in cancelled" : formatApiError(e);
      setError(m); throw new Error(m);
    }
  };

  const verifyMfa = async (mfaToken, method, code, trustDevice) => {
    const { data } = await api.post("/auth/mfa/verify", {
      mfa_token: mfaToken, method, code, trust_device: !!trustDevice,
    });
    const u = await applyPreferredRole(data.user);
    setUser(u);
    return u;
  };

  const sendMfaEmailOtp = async (mfaToken) => {
    await api.post("/auth/mfa/email-otp/send", { mfa_token: mfaToken });
  };

  const register = async (firstName, lastName, email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/register", { first_name: firstName, last_name: lastName || null, email, password });
      setUser(data.user);
      return data.user;
    } catch (e) {
      const m = formatApiError(e); setError(m); throw new Error(m);
    }
  };

  const tokenLogin = async (code) => {
    setError("");
    try {
      const { data } = await api.post("/auth/token-login", { code });
      if (isSandbox() && data.access_token) setImpToken(data.access_token);
      setUser(data.user);
      return data.user;
    } catch (e) {
      const m = formatApiError(e); setError(m); throw new Error(m);
    }
  };

  const studentLogin = async (firstName, lastName, studentId) => {
    setError("");
    try {
      const { data } = await api.post("/auth/student-login", { first_name: firstName || null, last_name: lastName || null, student_id: studentId });
      // Sandbox tabs authenticate via a per-tab token (no cookies), staying severed from admin.
      if (isSandbox() && data.access_token) setImpToken(data.access_token);
      // On first login we defer setUser so the reveal screen can render (setting user here would
      // trigger the /login -> /dashboard redirect). The auth cookie is already set by the server.
      if (!data.first_login) setUser(data.user);
      return data; // { user, first_login }
    } catch (e) {
      const m = formatApiError(e); setError(m); throw new Error(m);
    }
  };

  const logout = async () => {
    // Impersonation/sandbox tabs only end THIS tab's session — never touch the shared cookie,
    // so the admin tab and other user tabs stay logged in.
    if (isImpersonating() || isSandbox()) {
      const sandbox = isSandbox();
      clearImpToken();
      setUser(false);
      window.location.assign(sandbox ? "/student" : "/login");
      return;
    }
    try { await api.post("/auth/logout"); } catch (_) {}
    setUser(false);
  };

  const impersonating = isImpersonating();

  const switchRole = async (role) => {
    const { data } = await api.post("/auth/switch-role", { role });
    try { localStorage.setItem(lastRoleKey(data.id), role); } catch (_) {}
    setUser(data);
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, setUser, error, login, passkeyLogin, register, tokenLogin, studentLogin, logout, switchRole, verifyMfa, sendMfaEmailOtp, refreshMe: fetchMe, impersonating }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
